import { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { apiCache } from '../utils/cache';

/**
 * SiteDataContext — fetches global site data ONCE from /site/init.
 * Provides: settings, categories, categoryTree to ALL components.
 *
 * Replaces:
 *   - Header's GET /categories/tree call
 *   - Footer's GET /categories/ call
 *   - useSettings() hook's GET /settings/ call
 *
 * Result: 3 redundant API calls → 0. Only 1 call from App.js.
 */

const DEFAULT_SETTINGS = {
  site_name: 'FK It Solution',
  site_tagline: '',
  logo_url: '/images/logo.png',
  favicon_url: '',
  email: '',
  phone: '',
  phone_secondary: '',
  address: '',
  map_embed_url: '',
  facebook: '',
  twitter: '',
  instagram: '',
  linkedin: '',
  youtube: '',
  whatsapp: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  google_analytics_id: '',
};

const SiteDataContext = createContext({
  settings: DEFAULT_SETTINGS,
  categories: [],
  categoryTree: [],
  loading: true,
  error: null,
});

export const SiteDataProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    // Only fetch once per app lifetime
    if (fetched.current) return;
    fetched.current = true;

    const fetchSiteData = async () => {
      try {
        const { data } = await api.get('/site/init');
        setSettings({ ...DEFAULT_SETTINGS, ...data.settings });
        setCategories(Array.isArray(data.categories) ? data.categories : []);
        setCategoryTree(Array.isArray(data.category_tree) ? data.category_tree : []);
      } catch (err) {
        console.error('Failed to fetch site data:', err);
        setError(err);
        // Keep defaults — app still renders
      } finally {
        setLoading(false);
      }
    };

    fetchSiteData();
  }, []);

  return (
    <SiteDataContext.Provider value={{ settings, categories, categoryTree, loading, error }}>
      {children}
    </SiteDataContext.Provider>
  );
};

export const useSiteData = () => useContext(SiteDataContext);
export default SiteDataContext;
